import Flex from "@/components/atoms/Flex";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import UserRoleFilter from "@/components/molecules/UserRoleFilter";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import useLoadOptions from "@/hooks/useLoadOptions";
import ActivitySector from "@/interfaces/ActivitySector";
import CompanyStatus from "@/interfaces/CompanyStatus";
import { ITableFilterComponentProps } from "@/interfaces/Table";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { FC } from "react";
import { Button, Card, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { MultiValue } from "react-select";

const CompanyFilters: FC<ITableFilterComponentProps> = ({
  filters,
  onFiltersChange,
}) => {
  const user = useSelector(selectCurrentUser);
  const {
    loadCompanyStatusesOptions,
    loadActivitySectors,
    loadOpcosOptions,
    loadCompaniesOptions,
  } = useLoadOptions();

  const handleStatusesChange = (status: MultiValue<CompanyStatus>) =>
    onFiltersChange({
      status,
    });

  const handleUnknownStatusChange = () => {
    onFiltersChange({
      unknownStatus: !filters.unknownStatus,
    });
  };

  const handleReminderDatetimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onFiltersChange({
      reminderDatetime: e.target.value || null,
    });
  };

  const handleExcludedActivitySectorsChange = (
    excludedActivitySectors: MultiValue<ActivitySector>,
  ) => {
    const excludedIds = new Set(excludedActivitySectors.map(({ id }) => id));
    onFiltersChange({
      activitySectors: (filters.activitySectors ?? []).filter(
        ({ id }: ActivitySector) => !excludedIds.has(id),
      ),
      excludedActivitySectors,
    });
  };

  const handleIncludedActivitySectorsChange = (
    activitySectors: MultiValue<ActivitySector>,
  ) => {
    const includedIds = new Set(activitySectors.map(({ id }) => id));
    onFiltersChange({
      activitySectors,
      excludedActivitySectors: (filters.excludedActivitySectors ?? []).filter(
        ({ id }: ActivitySector) => !includedIds.has(id),
      ),
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      status: [],
      companies: [],
      sdrs: [],
      aes: [],
      activitySectors: [],
      excludedActivitySectors: [],
      opcos: [],
      unknownStatus: false,
      reminderDatetime: null,
      siret: "",
      city: "",
      zipCode: "",
      name: "",
      phone: "",
      interlocutor: "",
    });
    localStorage.removeItem("companiesFilters");
  };

  return (
    <Card className="shadow-none shadow-show-xl scrollbar h-100">
      <Card.Body>
        <Form>
          <Form.Group className="mt-n2">
            <Flex className="flex-between-center">
              <Form.Label className="mb-1 fs--1">Statuts</Form.Label>
            </Flex>
            <SelectPaginateAsync
              loadOptions={loadCompanyStatusesOptions}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id.toString()}
              value={filters.status}
              onChange={handleStatusesChange}
              isMulti
              isClearable={false}
            />
            <Form.Check
              checked={!!filters.unknownStatus}
              onChange={handleUnknownStatusChange}
              label="Inconnu"
              className="my-2"
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Entreprises :</Form.Label>
            <SelectPaginateAsync
              loadOptions={loadCompaniesOptions}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id.toString()}
              value={filters.companies}
              isClearable
              isMulti
              onChange={(companies) => onFiltersChange({ companies })}
            />
          </Form.Group>
          <UserRoleFilter
            role="AE"
            source="all"
            value={filters.aes}
            onChange={(aes) => onFiltersChange({ aes })}
          />
          <UserRoleFilter
            role="SDR"
            source="all"
            value={filters.sdrs}
            onChange={(sdrs) => onFiltersChange({ sdrs })}
          />
          <Form.Group className="mt-2">
            <Form.Label
              htmlFor="included-activity-sectors"
              className="mb-1 fs--1"
            >
              Secteurs inclus :
            </Form.Label>
            <SelectPaginateAsync
              inputId="included-activity-sectors"
              loadOptions={loadActivitySectors}
              getOptionLabel={(option) =>
                `${option.opco?.name} - ${option.name}`
              }
              getOptionValue={(option) => option.id.toString()}
              value={filters.activitySectors}
              isClearable
              isMulti
              onChange={handleIncludedActivitySectorsChange}
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label
              htmlFor="excluded-activity-sectors"
              className="mb-1 fs--1"
            >
              Secteurs exclus :
            </Form.Label>
            <SelectPaginateAsync
              inputId="excluded-activity-sectors"
              loadOptions={loadActivitySectors}
              getOptionLabel={(option) =>
                `${option.opco?.name} - ${option.name}`
              }
              getOptionValue={(option) => option.id.toString()}
              value={filters.excludedActivitySectors}
              isClearable
              isMulti
              onChange={handleExcludedActivitySectorsChange}
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">OPCO :</Form.Label>
            <SelectPaginateAsync
              loadOptions={loadOpcosOptions}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id.toString()}
              value={filters.opcos}
              isClearable
              isMulti
              onChange={(opcos) => onFiltersChange({ opcos })}
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Rappel avant le :</Form.Label>
            <Form.Control
              type="datetime-local"
              value={filters.reminderDatetime || ""}
              onChange={handleReminderDatetimeChange}
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Siret :</Form.Label>
            <Form.Control
              type="text"
              value={filters.siret || ""}
              onChange={(e) =>
                onFiltersChange({ siret: e.target.value || null })
              }
              placeholder="Rechercher par siret..."
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Interlocuteur :</Form.Label>
            <Form.Control
              type="text"
              value={filters.interlocutor || ""}
              onChange={(e) =>
                onFiltersChange({ interlocutor: e.target.value || null })
              }
              placeholder="Rechercher par interlocuteur..."
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Ville :</Form.Label>
            <Form.Control
              type="text"
              value={filters.city || ""}
              onChange={(e) =>
                onFiltersChange({ city: e.target.value || null })
              }
              placeholder="Rechercher par ville..."
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Code postal :</Form.Label>
            <Form.Control
              type="text"
              value={filters.zipCode || ""}
              onChange={(e) =>
                onFiltersChange({ zipCode: e.target.value || null })
              }
              placeholder="Rechercher par code postal..."
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Nom :</Form.Label>
            <Form.Control
              type="text"
              value={filters.name || ""}
              onChange={(e) =>
                onFiltersChange({ name: e.target.value || null })
              }
              placeholder="Rechercher par nom..."
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Form.Label className="mb-1 fs--1">Téléphone :</Form.Label>
            <Form.Control
              type="text"
              value={filters.phone || ""}
              onChange={(e) =>
                onFiltersChange({ phone: e.target.value || null })
              }
              placeholder="Rechercher par téléphone..."
            />
          </Form.Group>
          <Form.Group className="mt-2">
            <Button variant="primary" onClick={handleClearFilters}>
              Réinitialisé
            </Button>
          </Form.Group>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CompanyFilters;
